import React from 'react'
import { useDispatch } from 'react-redux'
import { Container, Box, ThemeProvider, CssBaseline } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import ErrorBoundary from './components/ErrorBoundary'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import Hero from './components/layout/Hero'
import AppRoutes from './components/AppRoutes'
import { ToastProvider } from './components/ToastProvider'
import { setLanguage } from './store/slices/languageSlice'
import { useThemeMode } from './hooks/useThemeMode'
import { createAppTheme } from './lib/theme'

const App: React.FC = () => {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [mode, toggleMode] = useThemeMode()
  const theme = React.useMemo(() => createAppTheme(mode), [mode])

  const handleLanguageChange = React.useCallback((lang: 'en' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }, [dispatch, i18n])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header mode={mode} onToggleTheme={toggleMode} onLanguageChange={handleLanguageChange} />
          <Hero title={t('title')} subtitle={t('subtitle')} />
          <Container
            key={location.pathname}
            maxWidth="xl"
            disableGutters
            sx={{ flex: 1, py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 }, opacity: 0, animation: 'routeFade 360ms ease forwards' }}
          >
            <AppRoutes location={location} />
          </Container>
          <Footer />
        </Box>
        <ToastProvider />
      </ErrorBoundary>
    </ThemeProvider>
  )
}

export default App
